import React, { createContext, useContext, useState, useEffect } from "react";
import { useAuth } from "./AuthContext";
import { useToast } from "../components/ui/use-toast";

// Minimal cart item structure
export interface CartItem {
  id: string;
  title: string;
  description?: string;
  price: number;
  quantity: number;
  image?: string;
  productId?: string;
  sizeId?: string;
  floristId: string;
  floristName?: string;
  size?: string;
}

interface CartContextType {
  items: CartItem[];
  addItem: (item: Omit<CartItem, "quantity">) => void;
  removeItem: (id: string) => void;
  updateQuantity: (id: string, quantity: number) => void;
  clear: () => void;
  total: number;
  loading: boolean;
  itemCount: number;
}

const STORAGE_KEY = 'bloomspace_cart';

const CartContext = createContext<CartContextType | undefined>(undefined);

export const CartProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [items, setItems] = useState<CartItem[]>([]);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();
  
  // Load cart from localStorage on mount
  useEffect(() => {
    try {
      const storedCart = localStorage.getItem(STORAGE_KEY);
      if (storedCart) {
        const parsedCart = JSON.parse(storedCart);
        setItems(parsedCart);
      }
    } catch (e) {
      localStorage.removeItem(STORAGE_KEY);
    } finally {
      setLoading(false);
    }
  }, []);

  // Save cart changes to localStorage
  useEffect(() => {
    if (!loading) {  // Only save after initial load
      try {
        if (items.length === 0) {
          localStorage.removeItem(STORAGE_KEY);
        } else {
          localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
        }
      } catch (e) {
        console.error('Error saving cart:', e);
      }
    }
  }, [items, loading]);

  const addItem = (newItem: Omit<CartItem, "quantity">) => {
    const existingItemIndex = items.findIndex(
      item => item.id === newItem.id || 
             (item.productId === newItem.productId && 
              item.sizeId === newItem.sizeId)
    );
    
    if (existingItemIndex > -1) {
      const updatedItems = [...items];
      updatedItems[existingItemIndex] = {
        ...updatedItems[existingItemIndex],
        quantity: updatedItems[existingItemIndex].quantity + 1
      };
      setItems(updatedItems);
    } else {
      const itemWithQuantity = { 
        ...newItem, 
        quantity: 1, 
        id: newItem.id || Math.random().toString(36).substring(2, 15)
      };
      setItems(prevItems => [...prevItems, itemWithQuantity]);
    }
  };

  const removeItem = (id: string) => {
    setItems(prevItems => prevItems.filter(item => item.id !== id));
    
    toast({
      title: "Removed from cart",
      description: "Item has been removed from your cart.",
      className: "border-brand-primary/20 bg-white text-[#4A4F41]"
    });
  };

  const updateQuantity = (id: string, quantity: number) => {
    if (quantity < 1) {
      removeItem(id);
      return;
    }
    
    setItems(prevItems => 
      prevItems.map(item => 
        item.id === id ? { ...item, quantity } : item
      )
    );
  };

  const clear = () => {
    setItems([]);
    localStorage.removeItem(STORAGE_KEY);
    
    // Only show toast when clearing manually, not during checkout
    if (window.location.pathname !== '/checkout-success') {
      toast({
        title: "Cart cleared",
        description: "All items have been removed from your cart.",
        className: "border-brand-primary/20 bg-white text-[#4A4F41]",
        duration: 2000 // Shorter duration
      });
    }
  };

  // Calculate total
  const total = items.reduce(
    (sum, item) => sum + (item.price || 0) * (item.quantity || 1),
    0
  );

  // Calculate item count
  const itemCount = items.reduce(
    (sum, item) => sum + (item.quantity || 1),
    0
  );

  const value = {
    items,
    addItem,
    removeItem,
    updateQuantity,
    clear,
    total,
    loading,
    itemCount
  };

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (context === undefined) {
    throw new Error("useCart must be used within a CartProvider");
  }
  return context;
};